import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { getUserToken } from '@utils/userUtils';
import { resolveGraphqlBaseUrl } from '@utils/tenant-utils';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { getError } from '@utils/graph-utils';
import { drawerSections } from './content';
import { ITreatyDocDrawerValues } from '.';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';

interface IAddTreatyDocumentDrawerProps {
  currentTreatyId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddTreatyDocumentDrawer: React.FC<IAddTreatyDocumentDrawerProps> = ({
  currentTreatyId,
  isOpen,
  onSuccess,
  onClose,
}) => {
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const submitForm = async (values: ITreatyDocDrawerValues) => {
    setIsDisabled(true);
    setSubmitButtonState('loading');
    try {
      const formData = new FormData();

      formData.append('formFile', values.document, values.document.name);
      formData.append(
        'operations',
        JSON.stringify({
          query: `
                  mutation createTreatyDocuments(
                    $UploadedDocuments: Upload!
                    $DocumentName: String!
                    $CurrentTreatyID: String!
                  ) {
                    production {
                      actions {
                        createTreatyDocuments(
                          UploadedDocuments: $UploadedDocuments
                          DocumentName: $DocumentName
                          CurrentTreatyID: $CurrentTreatyID
                        ) {
                          id
                        }
                      }
                    }
                  }`,
          variables: {
            UploadedDocuments: null as string,
            DocumentName: values.documentName,
            CurrentTreatyID: currentTreatyId,
          },
        })
      );
      //   map the uploaded file to the variables.UploadedDocuments
      formData.append(
        'map',
        JSON.stringify({ formFile: ['variables.UploadedDocuments'] })
      );

      const options = {
        method: 'POST',
        headers: {
          authorization: `Bearer ${getUserToken()}` || null,
          'GraphQL-preflight': '1',
        },
        body: formData,
      };

      try {
        const response = await fetch(
          `${resolveGraphqlBaseUrl()}/graphql`,
          options
        );

        const result = await response?.json();

        if (!result.errors) {
          toast.success(
            <ToastSuccessMessage>
              {'Document successfully uploaded'}
            </ToastSuccessMessage>
          );

          setTimeout(() => {
            setSubmitButtonState('success');
            onSuccess();
            onClose();
          }, 500);
        } else {
          setSubmitButtonState(undefined);
          toast.error(
            <ToastErrorMessage>{getError(response)}</ToastErrorMessage>
          );
        }
      } catch (error) {
        setSubmitButtonState(undefined);
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      }
    } catch {
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      setIsDisabled(false);
    }
  };

  return (
    <DynamicFormDrawer
      title={'Add Document'}
      onClose={() => onClose()}
      isOpen={isOpen}
      sections={drawerSections}
      disableForm={isDisabled}
      isSubmitButtonDisabled={isDisabled}
      submitButtonState={submitButtonState}
      onSubmit={(v: ITreatyDocDrawerValues) => submitForm(v)}
    />
  );
};

export default AddTreatyDocumentDrawer;
